import { useState } from 'react'
import { CurrencyInput } from '@/components/CurrencyInput'
import type { CreateOperatingCostMonthlyEntryInput } from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostMonthlyEntry } from '@/types/api'
import {
  formatOperatingCostAmount,
  formatOperatingCostMonth,
  OPERATING_COST_MONTH_OPTIONS,
  operatingCostYearMax,
  OPERATING_COST_YEAR_MIN,
  parseAmountInput,
  parseMonthInput,
  parseYearInput,
} from '../lib/operating-costs'
import styles from '../CustosOperacionaisPage.module.css'

type AwsMonthlyEntryRowProps = {
  entry: OperatingCostMonthlyEntry
  isSaving: boolean
  onUpdate: (entryId: number, input: CreateOperatingCostMonthlyEntryInput) => Promise<boolean>
  onDelete: (entryId: number) => Promise<boolean>
}

export function AwsMonthlyEntryRow({
  entry,
  isSaving,
  onUpdate,
  onDelete,
}: AwsMonthlyEntryRowProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(false)
  const [month, setMonth] = useState(String(entry.month))
  const [year, setYear] = useState(String(entry.year))
  const [amount, setAmount] = useState(String(entry.amount))
  const [rowError, setRowError] = useState<string | null>(null)
  const yearMax = operatingCostYearMax()

  if (isEditing) {
    return (
      <form
        className={styles.monthlyRowEdit}
        onSubmit={(event) => {
          event.preventDefault()
          const parsedMonth = parseMonthInput(month)
          const parsedYear = parseYearInput(year)
          const parsedAmount = parseAmountInput(amount)
          if (parsedMonth === null) {
            setRowError('Informe um mês entre janeiro e dezembro.')
            return
          }
          if (parsedYear === null) {
            setRowError(`Informe um ano entre ${OPERATING_COST_YEAR_MIN} e ${yearMax}.`)
            return
          }
          if (parsedAmount === null) {
            setRowError('Informe o valor em reais.')
            return
          }
          setRowError(null)
          void onUpdate(entry.id, {
            month: parsedMonth,
            year: parsedYear,
            amount: parsedAmount,
          }).then((ok) => {
            if (ok) setIsEditing(false)
          })
        }}
      >
        <label className={styles.field} htmlFor={`edit-aws-month-${entry.id}`}>
          Mês
          <select
            id={`edit-aws-month-${entry.id}`}
            className={styles.input}
            value={month}
            disabled={isSaving}
            onChange={(event) => setMonth(event.target.value)}
          >
            {OPERATING_COST_MONTH_OPTIONS.map((option) => (
              <option key={option.value} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field} htmlFor={`edit-aws-year-${entry.id}`}>
          Ano
          <input
            id={`edit-aws-year-${entry.id}`}
            className={styles.input}
            inputMode="numeric"
            value={year}
            min={OPERATING_COST_YEAR_MIN}
            max={yearMax}
            maxLength={4}
            autoComplete="off"
            disabled={isSaving}
            onChange={(event) => setYear(event.target.value)}
          />
        </label>
        <label className={styles.field} htmlFor={`edit-aws-amount-${entry.id}`}>
          Valor
          <CurrencyInput
            id={`edit-aws-amount-${entry.id}`}
            value={amount}
            disabled={isSaving}
            onChange={setAmount}
          />
        </label>
        <button type="submit" className={styles.secondaryButton} disabled={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </button>
        <button
          type="button"
          className={styles.ghostButton}
          disabled={isSaving}
          onClick={() => {
            setIsEditing(false)
            setMonth(String(entry.month))
            setYear(String(entry.year))
            setAmount(String(entry.amount))
            setRowError(null)
          }}
        >
          Cancelar
        </button>
        {rowError ? (
          <p className={styles.inlineError} role="alert">
            {rowError}
          </p>
        ) : null}
      </form>
    )
  }

  return (
    <div className={styles.entryRow}>
      <div className={styles.entryMeta}>
        <span className={styles.entrySubtitle}>
          {formatOperatingCostMonth(entry.month)} de {entry.year}
        </span>
        <span className={styles.entryValue}>{formatOperatingCostAmount(entry.amount)}</span>
      </div>
      <div className={styles.entryActions}>
        <button
          type="button"
          className={styles.ghostButton}
          disabled={isSaving}
          onClick={() => {
            setIsEditing(true)
            setPendingDelete(false)
          }}
        >
          Editar
        </button>
        {pendingDelete ? (
          <>
            <button
              type="button"
              className={styles.dangerButton}
              disabled={isSaving}
              onClick={() => {
                void onDelete(entry.id)
              }}
            >
              {isSaving ? 'Excluindo...' : 'Confirmar'}
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              disabled={isSaving}
              onClick={() => setPendingDelete(false)}
            >
              Cancelar
            </button>
          </>
        ) : (
          <button
            type="button"
            className={styles.ghostButton}
            disabled={isSaving}
            onClick={() => setPendingDelete(true)}
          >
            Excluir
          </button>
        )}
      </div>
    </div>
  )
}
